import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  colors,
  Divider,
  FormControlLabel,
  Link,
  makeStyles,
  Modal,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import clsx from "clsx";
import _ from "lodash";
import React, { ReactElement, useState } from "react";

import { AddIcon } from "../components/icons/themes/windows10";
import { useModalStyles } from "../destinations/modals/styles";
import useAccountMetadata from "../hooks/useAccountMetadata";
import { PropertySensitivityLevels, SensitivityLevel } from "../types/global";
import { isAccountInHIPAAModeV2 } from "../util";
import { InfoOutlined } from "@material-ui/icons";

const useStyles = makeStyles((_) => ({
  docsCallout: {
    backgroundColor: colors.blue[50],
    borderRadius: "0.25em",
    color: colors.grey[700],
    marginTop: "1em",
    padding: "0.5em",
    textAlign: "center",
  },
  lightText: {
    color: colors.grey[200],
  },
  tooltipInfoIcon: {
    marginLeft: "4px",
    marginTop: "-2px",
    cursor: "pointer",
  },
}));

// The list of built-in props to offer for the allowlist.
export const allowlistBuiltInProps: Record<
  string,
  { beautifulName: string }
> = {
  $title: { beautifulName: "Page Title" },
  $current_url: { beautifulName: "URL" },
  utm_source: { beautifulName: "UTM Source" },
  utm_content: { beautifulName: "UTM Content" },
  utm_term: { beautifulName: "UTM Term" },
  utm_campaign: { beautifulName: "UTM Campaign" },
  utm_medium: { beautifulName: "UTM Medium" },
  $query_params: { beautifulName: "Other Query Parameters" },
  $pathname: { beautifulName: "Pathname" },
  $ip: { beautifulName: "IP Address" },
  $user_agent: { beautifulName: "User Agent" },
  $initial_referrer: { beautifulName: "Initial Referrer" },
  $initial_referring_domain: { beautifulName: "Initial Referring Domain" },
  $referrer: { beautifulName: "Referrer" },
  $referring_domain: { beautifulName: "Referring Domain" },
  $gclid: { beautifulName: "Google Ad Click ID (GCLID)" },
  $dclid: { beautifulName: "Floodlight Click ID (DCLID)" },
  $gclsrc: { beautifulName: "Google Click Source (GCLSRC)" },
  $auiddc: { beautifulName: "DoubleClick Cookie ID (AUIDDC)" },
  $gbraid: { beautifulName: "Google Ads GBRAID" },
  $wbraid: { beautifulName: "Google Ads WBRAID" },
  $fbclid: { beautifulName: "Facebook Ad Click ID" },
  $msclkid: { beautifulName: "Bing Ad Click ID" },
  $twclid: { beautifulName: "Twitter Ad Click ID" },
  $ttclid: { beautifulName: "TikTok Ad Click ID" },
  $irclickid: { beautifulName: "impact.com Click ID" },
  $li_fat_id: { beautifulName: "LinkedIn Ads Click ID (LI_FAT_ID)" },
  $rdt_cid: { beautifulName: "Reddit Ads Click ID (rdt_cid)" },
  $epik: { beautifulName: "Pinterest Ads Click ID (EPIK)" },
  $cntr_auctionId: { beautifulName: "Basis Ad Click ID (cntr_auctionId)" },
};

const allowlistBuiltInPropDisplayOrder: string[] = [
  "$title",
  "$current_url",
  "utm_source",
  "utm_content",
  "utm_term",
  "utm_campaign",
  "utm_medium",
  "$query_params",
  "$pathname",
  "$ip",
  "$user_agent",
  "$initial_referrer",
  "$initial_referring_domain",
  "$referrer",
  "$referring_domain",
  "$gclid",
  "$dclid",
  "$gclsrc",
  "$auiddc",
  "$gbraid",
  "$wbraid",
  "$fbclid",
  "$msclkid",
  "$twclid",
  "$ttclid",
  "$irclickid",
  "$li_fat_id",
  "$rdt_cid",
  "$epik",
  "$cntr_auctionId",
];

function isBuiltInProp(propName: string): boolean {
  return !!allowlistBuiltInProps[propName];
}

interface TwoColumnLayoutProps<T> {
  items: T[];
  renderItem: (item: T) => ReactElement;
}

const TwoColumnLayout = <T extends unknown>({
  items,
  renderItem,
}: TwoColumnLayoutProps<T>) => {
  const numRows = Math.ceil(items.length / 2);
  return (
    <Box
      sx={{
        display: "grid",
        gridAutoFlow: "column",
        gridTemplateColumns: "1fr 1fr",
        gridTemplateRows: `repeat(${numRows}, 1fr)`,
      }}
    >
      {_.map(items, renderItem)}
    </Box>
  );
};

interface PHIAllowListEditModalProps {
  allowListType: string;
  propertySensitivityLevels: PropertySensitivityLevels;
  open: boolean;
  onSave: (newSensitivityLevels: PropertySensitivityLevels) => Promise<void>;
  onClose: () => void;
}

export const PHIAllowListEditModal: React.FC<PHIAllowListEditModalProps> = ({
  allowListType,
  propertySensitivityLevels,
  open,
  onSave,
  onClose,
}) => {
  const accountMetadata = useAccountMetadata();
  const classes = useStyles();
  const modalClasses = useModalStyles();

  // In HIPAA mode V2, display builtin properties on the allowlist separately
  let initialBuiltInPropNames: string[] = [];
  let initialCustomPropNames = Object.keys(propertySensitivityLevels);
  if (isAccountInHIPAAModeV2(accountMetadata)) {
    [initialBuiltInPropNames, initialCustomPropNames] = _.partition(
      Object.keys(propertySensitivityLevels),
      isBuiltInProp
    );
  }
  const [builtInPropsState, setBuiltInPropsState] = useState(
    _.pick(propertySensitivityLevels, initialBuiltInPropNames)
  );
  const initialCustomProps: [string, SensitivityLevel][] =
    Object.keys(propertySensitivityLevels).length != 0
      ? Object.entries(
          _.pick(propertySensitivityLevels, initialCustomPropNames)
        )
      : [["", SensitivityLevel.NotSensitive]];
  const [customPropsState, setCustomPropsState] = useState(initialCustomProps);

  const onDeleteCustomProp = (index: number) => {
    const newProps = customPropsState.slice();
    newProps.splice(index, 1);
    setCustomPropsState(newProps);
  };

  const onChangeCustomProp = (
    index: number,
    newProp: string,
    newSensitivityLevel: SensitivityLevel
  ) => {
    const newProps = customPropsState.slice();
    newProps[index] = [newProp, newSensitivityLevel];
    setCustomPropsState(newProps);
  };

  const onChangeBuiltinProp = (propName: string) => {
    setBuiltInPropsState((state) => {
      if (state[propName]) {
        delete state[propName];
      } else {
        state[propName] = SensitivityLevel.NotSensitive;
      }
      // need to return a new object so that react knows
      // there was an update
      return { ...state };
    });
  };

  return (
    <Modal open={open}>
      <Card className={clsx(modalClasses.root, modalClasses.smallModal)}>
        <CardHeader
          title={`Allow List: ${allowListType}`}
          classes={{ action: modalClasses.closeIconAction }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          {isAccountInHIPAAModeV2(accountMetadata) && (
            <>
              <Typography variant="h5">Built-in properties</Typography>
              <TwoColumnLayout
                items={allowlistBuiltInPropDisplayOrder}
                renderItem={(propName) => (
                  <FormControlLabel
                    key={propName}
                    label={allowlistBuiltInProps[propName].beautifulName}
                    control={
                      <Checkbox
                        checked={!!builtInPropsState[propName]}
                        onClick={() => onChangeBuiltinProp(propName)}
                      />
                    }
                  />
                )}
              />
              <Typography variant="h5">Custom Properties</Typography>
            </>
          )}
          <div className="justify-items-stretch" style={{ width: "100%" }}>
            {customPropsState.map(([prop, sensitivityLevel], index) => {
              const isHashed =
                sensitivityLevel == SensitivityLevel.NotSensitiveIfHashed;
              return (
                <div className="flex items-center my-2" key={index}>
                  <span>
                    <Tooltip title="Remove property from allow list">
                      <div
                        className="hover:bg-navy-200 p-1 rounded-full"
                        onClick={() => onDeleteCustomProp(index)}
                      >
                        <CloseIcon />
                      </div>
                    </Tooltip>
                  </span>
                  <span className="mx-1">
                    <TextField
                      fullWidth
                      autoComplete="off"
                      name={"Allowlisted Property"}
                      placeholder={"Allowlisted Property"}
                      onChange={(event: any) =>
                        onChangeCustomProp(
                          index,
                          event.target.value,
                          sensitivityLevel
                        )
                      }
                      value={prop || ""}
                      key={index}
                      variant="outlined"
                      type={"text"}
                      inputProps={{ style: { fontFamily: "monospace" } }}
                      className="fs-exclude"
                    />
                  </span>
                  <span className="mx-1">
                    <Checkbox
                      checked={isHashed}
                      onChange={(_) => {
                        const newSensitivityLevel = isHashed
                          ? SensitivityLevel.NotSensitive
                          : SensitivityLevel.NotSensitiveIfHashed;
                        onChangeCustomProp(index, prop, newSensitivityLevel);
                      }}
                      name={index.toString()}
                    />
                    <span>
                      <Tooltip
                        title={
                          <Typography
                            className={clsx(classes.lightText)}
                            component={"span"}
                            variant={"body1"}
                          >
                            When enabled, Freshpaint will apply a cryptographic
                            hash to the property before sending to the
                            destination.
                          </Typography>
                        }
                      >
                        <Typography component={"span"} variant={"body1"}>
                          Masked
                          <InfoOutlined
                            className={clsx(classes.tooltipInfoIcon)}
                            fontSize={"small"}
                          />
                        </Typography>
                      </Tooltip>
                    </span>
                  </span>
                </div>
              );
            })}
            <Button
              color="primary"
              className="text-xs bg-navy-100 text-navy-800 inline-block inline-flex items-center px-3 py-1 rounded cursor-pointer hover:bg-navy-200"
              onClick={() =>
                setCustomPropsState([
                  ...customPropsState,
                  ["", SensitivityLevel.NotSensitive],
                ])
              }
            >
              <AddIcon className="mr-1" style={{ width: 16 }} />
              Add Property
            </Button>

            <Typography
              className={classes.docsCallout}
              component={"p"}
              variant={"body1"}
            >
              Learn more about HIPAA Mode and ID masking{" "}
              <Link
                href={
                  "https://documentation.freshpaint.io/integrations/hipaa-mode#id-masking"
                }
                target={"_blank"}
                rel={"noopener noreferrer"}
              >
                in our docs.
              </Link>
            </Typography>
          </div>
        </CardContent>
        <Divider />
        <CardActions className={modalClasses.actions}>
          <Button onClick={onClose}>Close</Button>
          <Button
            color="primary"
            onClick={() =>
              onSave(
                _.merge(
                  {},
                  builtInPropsState,
                  Object.fromEntries(customPropsState)
                )
              )
            }
            variant="contained"
          >
            Save
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
};
